/**
 * 大气渲染器：全屏 quad + 大气散射着色器，负责把 HDR 大气/地表合成结果写入目标 RT。
 */

import * as THREE from 'three'
import type { AtmosphereCoeffs } from '../utils/atmosphereParams'
import { SUN_IRRADIANCE, SUN_ANGULAR_RADIUS } from '../utils/atmosphereParams'
import type { ScatteringMode } from './types'
import { SCATTERING_MODE_INDEX } from './types'
import { goldenSpiralDirections, MAX_MS_DIRS } from '../utils/msDirections'
import { fullscreenVertexShader } from './shaders/fullscreen.glsl'
import { atmosphereFragmentShader } from './shaders/atmosphere.glsl'

export class AtmosphereRenderer {
  readonly material: THREE.ShaderMaterial
  private scene = new THREE.Scene()
  private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)

  constructor() {
    const uniforms: Record<string, THREE.IUniform> = {
      uResolution: { value: new THREE.Vector2(1, 1) },
      uCamPos: { value: new THREE.Vector3() },
      uCamRight: { value: new THREE.Vector3(1, 0, 0) },
      uCamUp: { value: new THREE.Vector3(0, 1, 0) },
      uCamForward: { value: new THREE.Vector3(0, 0, -1) },
      uTanHalfFov: { value: new THREE.Vector2(1, 1) },
      uSunDir: { value: new THREE.Vector3(0, 1, 0) },
      uTransLUT: { value: null },
      uSkyAmbient: { value: new THREE.Vector3(0.02, 0.03, 0.05) },
      uBetaR: { value: new THREE.Vector3() },
      uBetaM: { value: new THREE.Vector3() },
      uBetaMExt: { value: new THREE.Vector3() },
      uHr: { value: 8 },
      uHm: { value: 1.2 },
      uPlanetR: { value: 6371 },
      uAtmosR: { value: 6471 },
      uG: { value: 0.76 },
      uSunAngularRadius: { value: SUN_ANGULAR_RADIUS },
      uSunIrradiance: { value: SUN_IRRADIANCE },
      uSampleSteps: { value: 32 },
      uScatteringMode: { value: 0 },
      uMSIntensity: { value: 1 },
      uMSDirCount: { value: 8 },
      uMSDirs: {
        value: goldenSpiralDirections(MAX_MS_DIRS).map(
          (d) => new THREE.Vector3(...d),
        ),
      },
      uMSLUT: { value: null },
      uRayleighOnly: { value: 0 },
      uMieOnly: { value: 0 },
      uNoScatter: { value: 0 },
    }

    this.material = new THREE.ShaderMaterial({
      vertexShader: fullscreenVertexShader,
      fragmentShader: atmosphereFragmentShader,
      uniforms,
      depthTest: false,
      depthWrite: false,
    })

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material)
    quad.frustumCulled = false
    this.scene.add(quad)
  }

  /** 更新物理系数与透射率 LUT */
  setCoeffs(coeffs: AtmosphereCoeffs, lutTexture: THREE.Texture): void {
    const u = this.material.uniforms
    u.uBetaR.value.fromArray(coeffs.betaR)
    u.uBetaM.value.fromArray(coeffs.betaM)
    u.uBetaMExt.value.fromArray(coeffs.betaMExt)
    u.uHr.value = coeffs.Hr
    u.uHm.value = coeffs.Hm
    u.uPlanetR.value = coeffs.R
    u.uAtmosR.value = coeffs.Rt
    u.uG.value = coeffs.g
    u.uSunDir.value.fromArray(coeffs.sunDir)
    u.uTransLUT.value = lutTexture
    u.uSkyAmbient.value.copy(coeffs.skyAmbient)
  }

  /** 更新相机基与视场（由 SceneManager 每帧传入） */
  setCameraBasis(
    pos: THREE.Vector3,
    right: THREE.Vector3,
    up: THREE.Vector3,
    forward: THREE.Vector3,
    tanHalfFov: THREE.Vector2,
  ): void {
    const u = this.material.uniforms
    u.uCamPos.value.copy(pos)
    u.uCamRight.value.copy(right)
    u.uCamUp.value.copy(up)
    u.uCamForward.value.copy(forward)
    u.uTanHalfFov.value.copy(tanHalfFov)
  }

  /** 更新渲染/教学参数 */
  setParams(p: {
    sampleSteps: number
    scatteringMode: ScatteringMode
    msIntensity: number
    msDirCount: number
    rayleighOnly: boolean
    mieOnly: boolean
    noScatter: boolean
  }): void {
    const u = this.material.uniforms
    u.uSampleSteps.value = p.sampleSteps
    u.uScatteringMode.value = SCATTERING_MODE_INDEX[p.scatteringMode]
    u.uMSIntensity.value = p.msIntensity
    u.uMSDirCount.value = p.msDirCount
    u.uRayleighOnly.value = p.rayleighOnly ? 1 : 0
    u.uMieOnly.value = p.mieOnly ? 1 : 0
    u.uNoScatter.value = p.noScatter ? 1 : 0
  }

  /** 绑定多次散射 LUT 纹理 */
  setMSLUT(texture: THREE.Texture): void {
    this.material.uniforms.uMSLUT.value = texture
  }

  /** 渲染大气 pass 到 target（地表颜色由着色器内解析计算） */
  render(
    renderer: THREE.WebGLRenderer,
    target: THREE.WebGLRenderTarget,
    resolution: THREE.Vector2,
  ): void {
    const u = this.material.uniforms
    u.uResolution.value.copy(resolution)

    const prev = renderer.getRenderTarget()
    renderer.setRenderTarget(target)
    renderer.render(this.scene, this.camera)
    renderer.setRenderTarget(prev)
  }

  dispose(): void {
    this.material.dispose()
  }
}
